import { createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'api/apiCommon'

import {
  type RejectRequestPayload,
  type StakeholderLimit,
  type StakeholderRequest,
} from '../requests/request.types'

interface GetRequestsPayload {
  ipp?: number
  page?: number
  isAdmin?: boolean
  status?: 'active' | 'resolved'
}

export const getRequests = createAsyncThunk(
  'reports/request/list',
  async ({ page, ipp, isAdmin, status }: GetRequestsPayload): Promise<{data: StakeholderRequest[], count: number}> => {
    const statusQueryParam = status ? `?status=${status}` : '';
    const ippQueryParam = ipp ? `&ipp=${ipp}` : '';
    const pageQueryParam = page ? `&page=${page}` : '';

    const route = `reports/request/list${isAdmin ? '' : '/me'}${statusQueryParam}${ippQueryParam}${pageQueryParam}`;

    return await new Promise((resolve, reject) => {
      axios
        .get<{data: StakeholderRequest[], count: number}>(route)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

interface GetLimitsPayload {
  ipp: number
  page: number
  isAdmin?: boolean
  status?: 'active' | 'resolved'
}

export const getLimits = createAsyncThunk(
  'reports/plan/request/list',
  async ({ isAdmin, page, ipp, status }: GetLimitsPayload): Promise<{ data: StakeholderLimit[], count: number }> => {
    const statusQueryParam = status ? `?status=${status}` : '';
    const route = `reports/plan/request/list${isAdmin ? '' : '/me'}${statusQueryParam}${status ? '&' : '?'}ipp=${ipp}&page=${page}`

    return await new Promise((resolve, reject) => {
      axios
        .get<{ data: StakeholderLimit[], count: number }>(route)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)
export const resolveRequest = createAsyncThunk(
  'reports/request/resolve',
  async (requestId: number): Promise<number> => {
    return await new Promise<number>((resolve, reject) => {
      axios
        .put<StakeholderRequest[]>(`reports/request/${requestId}/resolve`)
        .then(() => {
          resolve(requestId)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)
export const resolveLimitRequest = createAsyncThunk(
  'reports/plan/request/resolve',
  async (requestId: number): Promise<number> => {
    return await new Promise<number>((resolve, reject) => {
      axios
        .put<StakeholderLimit[]>(`reports/plan/request/${requestId}/resolve`)
        .then(() => {
          resolve(requestId)
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)
export const rejectRequest = createAsyncThunk(
  'reports/requests/id/reject',
  async ({
    data,
    requestId,
  }: {
    data: RejectRequestPayload
    requestId: number
  }): Promise<{ data: RejectRequestPayload; requestId: number }> => {
    return await new Promise<{ data: RejectRequestPayload; requestId: number }>((resolve, reject) => {
      axios
        .post(`reports/request/${requestId}/reject`, data)
        .then(() => {
          resolve({ data, requestId })
        })
        .catch((err) => {
          reject(err)
        })
    })
  },
)

interface RequestPlanChangePayload {
  newSubscriptionLimit: number
  newRequestsLimit: number
}

export const requestPlanChange = createAsyncThunk(
  'reports/requestPlanChange',
  async (payload: RequestPlanChangePayload): Promise<void> => {
    await new Promise((resolve, reject) => {
      axios
        .post('reports/plan/request', payload)
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error('err: ', err)
          reject(err)
        })
    })
  },
)

interface EditPlanPayload {
  newSubscriptionLimit: number
  newRequestsLimit: number
  groupId: string
  endDate: string
}

export const editPlan = createAsyncThunk(
  'reports/editPlan',
  async (payload: EditPlanPayload): Promise<void> => {
    await new Promise((resolve, reject) => {
      axios
        .put(
          `${
            process.env.REACT_APP_REST_API_URL_REPORTING ?? process.env.REACT_APP_REST_API_URL ?? ''
          }/reports/plan/edit`,
          payload,
        )
        .then((data) => {
          resolve(data)
        })
        .catch((err) => {
          console.error('err: ', err)
          reject(err)
        })
    })
  },
)
